import React from 'react';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Alert, Box, Stack, Typography } from '@mui/material';

import { isRowApproved } from '../../manualOverridePersistence';
import HiddenApprovedFooter from '../../reportCards/HiddenApprovedFooter';
import ReportCard from '../../reportCards/ReportCard';
import type { ManualOverrideState } from '../../types';

import DealCard from './DealCard';
import { getDealOptionsInclusionsSearchText, getDealSearchText } from './dealOptionsInclusionsQueue';
import type { DealOptionRowView } from './types';

interface Props {
  dealRows: Array<DealOptionRowView>;
  expandedRows: Record<string, boolean>;
  hiddenApprovedCount: number;
  manualOverrides: ManualOverrideState;
  searchQuery: string;
  showAutoApproved: boolean;
  onApprovalChange: (rowId: string) => void;
  onToggleDetails: (rowId: string, defaultOpen?: boolean) => void;
  onUnapprovalChange: (rowId: string) => void;
}

export default function DealOptionsInclusionsReportCard({
  dealRows,
  expandedRows,
  hiddenApprovedCount,
  manualOverrides,
  searchQuery,
  showAutoApproved,
  onApprovalChange,
  onToggleDetails,
  onUnapprovalChange,
}: Props) {
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const visibleDealRows = !normalizedSearchQuery
    ? dealRows
    : dealRows.filter((deal) => {
        if (getDealSearchText(deal).toLowerCase().includes(normalizedSearchQuery)) {
          return true;
        }

        return deal.inclusions.some((inc) => {
          if (!showAutoApproved && isRowApproved(inc, manualOverrides)) {
            return false;
          }

          return getDealOptionsInclusionsSearchText(inc).toLowerCase().includes(normalizedSearchQuery);
        });
      });

  return (
    <ReportCard bordered={false}>
      <Stack spacing={2} sx={{ pb: 2 }}>
        <Alert severity="info" icon={<InfoOutlinedIcon fontSize="small" />}>
          <strong>RRP comparison:</strong> the dot is{' '}
          <Box component="span" sx={{ color: 'success.main', fontWeight: 700 }}>
            green
          </Box>{' '}
          when <strong>Inclusion Asset Value</strong> exactly equals <strong>Deal Option Inclusion RRP</strong>. It
          turns{' '}
          <Box component="span" sx={{ color: 'warning.main', fontWeight: 700 }}>
            orange
          </Box>{' '}
          when those values differ.
          <br />
          <strong>RRP Guest:</strong> a separate check — turns orange when the same inclusion has an identical RRP
          across deal options with a different number of included guests (e.g. 2A vs 6A), meaning the RRP likely
          wasn&rsquo;t scaled for the larger room. Expand a flagged inclusion to see which deal option it matches.
        </Alert>

        {visibleDealRows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
            {normalizedSearchQuery ? 'No deal options match your search.' : 'No deal options found.'}
          </Typography>
        ) : (
          visibleDealRows.map((deal) => (
            <DealCard
              key={deal.id}
              deal={deal}
              expandedRows={expandedRows}
              manualOverrides={manualOverrides}
              searchQuery={searchQuery}
              showAutoApproved={showAutoApproved}
              onApprovalChange={onApprovalChange}
              onToggleDetails={onToggleDetails}
              onUnapprovalChange={onUnapprovalChange}
            />
          ))
        )}
      </Stack>

      <HiddenApprovedFooter count={hiddenApprovedCount} noun="inclusion row" showAutoApproved={showAutoApproved} />
    </ReportCard>
  );
}
